import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AddBillPdfMethodAutomaticParsingModal } from "@features/AddBillPdfMethodAutomaticParsingModal/AddBillPdfMethodAutomaticParsingModal";
import { AddHouseModal } from "@features/AddHouseModal/AddHouseModal";
import { Button } from "@components/Button";
import { FileUploadButton } from "@components/FileUploadButton/FileUploadButton";
import { DocumentIcon, HouseIcon, HousePlusIcon } from "@components/icons";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useModal } from "@hooks/useModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useUploadBillFileMutation } from "@services/billFiles/mutations";
import { BillFileParsingState } from "@services/billFiles/types";
import { useOverviewQuery } from "@services/overview/queries";
import { fileToBase64 } from "@utils/common";
import {
  useAddBillPdfMethodModalSchema,
  type TAddBillPdfMethodModalSchema,
} from "./useAddBillPdfMethodModalSchema";

interface IAddBillPdfMethodModalProps extends IUseModalProps {
  onCancel?: () => void;
  preselectedJointBillServiceId?: number;
}

export const AddBillPdfMethodModal: React.FC<IAddBillPdfMethodModalProps> = (
  props,
) => {
  const { onCancel, preselectedJointBillServiceId, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const { create } = useModalStore();

  const { data: overviewData } = useOverviewQuery();

  const availableHouses = overviewData?.jointBillServices.map(
    (jointBillService) => ({
      value: jointBillService.jointBillServiceId,
      label: jointBillService.displayName ?? t("common.defaultAddress"),
    }),
  );

  const schema = useAddBillPdfMethodModalSchema();

  const { handleSubmit, control, setValue, watch } =
    useForm<TAddBillPdfMethodModalSchema>({
      defaultValues: {
        house: preselectedJointBillServiceId ?? availableHouses?.[0]?.value,
      },
      resolver: zodResolver(schema),
    });

  const openAddHouseModal = () =>
    create((modalProps) => (
      <AddHouseModal
        {...modalProps}
        onSuccess={(jointBillServiceId) =>
          setValue("house", jointBillServiceId)
        }
      />
    ));

  const file = watch("file");

  const { mutateAsync, isPending, data, isError } = useUploadBillFileMutation();

  const onSubmit: SubmitHandler<TAddBillPdfMethodModalSchema> = async (
    data,
  ) => {
    const fileContentBase64 = await fileToBase64(data.file);
    const result = await mutateAsync({
      fileContentBase64: fileContentBase64,
      jointBillServiceId: data.house,
    });

    if (result.billFileParsingState === BillFileParsingState.AutomaticParsing) {
      create((modalProps) => (
        <AddBillPdfMethodAutomaticParsingModal
          {...modalProps}
          file={file}
          billFileId={result.billFileId}
          serviceProviderId={result.serviceProviderId}
          serviceProviderName={result.serviceProviderName}
          defaultValues={{
            jointBillServiceId: data.house,
            customerCode: result.customerCode,
            total: result.total,
            iban: result.iban,
          }}
        />
      ));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: DocumentIcon,
        color: "yellow",
      }}
      title={t("addBillPdfMethodModal.title")}
      subtitle={t("addBillPdfMethodModal.subtitle")}
      actionButtons={
        <React.Fragment>
          <Button
            variant="secondary"
            onClick={() => {
              modal.close();
              onCancel?.();
            }}
            className="w-full"
          >
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            isLoading={isPending}
            onClick={
              data?.billFileParsingState === BillFileParsingState.ManualParsing
                ? modal.close
                : handleSubmit(onSubmit)
            }
          >
            {t("common.form.buttons.continue")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-5">
        <div>
          <FormSelect
            name="house"
            control={control}
            icon={HouseIcon}
            options={availableHouses ?? []}
            label={t("addBillPdfMethodModal.fields.house.label")}
          />
          <Button
            variant="secondary"
            className="mt-3 h-11 w-full"
            icon={HousePlusIcon}
            onClick={openAddHouseModal}
          >
            {t("addBillPdfMethodModal.addHouse")}
          </Button>
        </div>
        <FileUploadButton
          name="file"
          control={control}
          setValue={setValue}
          disabled={isPending}
        />
        {file && (
          <Notification
            color="green"
            icon={DocumentIcon}
            description={t("addBillPdfMethodModal.fields.file.addedFile", {
              fileName: file.name,
            })}
          />
        )}
        {data?.billFileParsingState === BillFileParsingState.ManualParsing && (
          <Notification
            description={t("addBillPdfMethodModal.result.manual")}
            color="green"
          />
        )}
        {isError && (
          <Notification
            description={t("addBillPdfMethodModal.result.error")}
            color="red"
          />
        )}
      </div>
    </Modal>
  );
};
